import { docReady, event as eventHandler, events as eventsHandler, action as actionHandler, actions as actionsHandler, bind as bindHandler } from './easyfy.core';
import { Id } from './easyfy.utils';

export interface Idependencies {
  func: { new(...args: any[]): {} };
  data?: any;
}

interface IActionEvent {
  target: string;
  type: string;
  meta?: object;
}

interface IInjectorDependencies {
  name: string;
  func: object;
}

interface IInjector {
  dependencies: {
    [key: string]: IInjectorDependencies;
  };
  resolveOnload: Array<string>;
  resolve: (depends: Array<Idependencies>, ctor: any, cb: Function) => void;
}

export const invoke = function <T extends { new(...args: any[]): {} }>(ctor: T): void {
  let newClass: Object;

  const readyAfter = () => {
    // call class function onInit
    try {
      ctor?.prototype?.onInit?.apply(newClass);
    } catch (error) {
      console.log('Invoke onInit error', error);
    }
  };

  const init = (resolves?: Array<Object>) => {
    try {
      newClass = new ctor(...(resolves || []));
    } catch (err) {
      console.log('Error ', err);
    }

    if (ctor.prototype.hasOwnProperty('onInit')) {
      // Use the handy event callback
      docReady(readyAfter);
    }
  };

  const depends = ctor['depends'];
  depends
    ? injector.resolve(ctor['depends'], ctor, (resolves) => {
      delete ctor['depends'];
      init(resolves);
    })
    : init();
};


const injector: IInjector = {
  dependencies: {},
  resolveOnload: [],
  resolve: function (depends: Array<Idependencies>, ctor: any, cb?: Function): void {
    depends.forEach((item) => {
      const id = Id(10);
      injector.dependencies[id] = { name: id, func: new item.func(item.data || {}) };
      if (injector.dependencies[id].func.hasOwnProperty('loaded')) {
        injector.resolveOnload.push(id);
      }
    });

    if (injector.resolveOnload.length > 0) {
      injector.resolveOnload.forEach((item, index) => {
        if (injector.dependencies[item].func.hasOwnProperty('loaded')) {
          const dependencyItem = injector.dependencies[item].func;

          let loadedVal = dependencyItem['loaded'];
          Object.defineProperty(dependencyItem, 'loaded', {
            get: () => {
              return loadedVal;
            },
            set: function (val) {
              if (val !== loadedVal) {
                if (val) {
                  if (dependencyItem) {
                    injector.resolveOnload.splice(index, 1);
                    delete dependencyItem['loaded'];

                    if (injector.resolveOnload.length === 0 && cb) {
                      let dependencyFunc: Array<Object> = [];
                      for (const key in injector.dependencies) {
                        if (injector.dependencies.hasOwnProperty(key)) {
                          const item = injector.dependencies[key];
                          dependencyFunc.push(item.func);
                        }
                      }
                      cb.call(this, dependencyFunc);
                    }
                  }
                }
              }

              loadedVal = val;
            },
            enumerable: true,
            configurable: true,
          });
        }
      });
    } else {
      cb?.call(this);
    }
  },
};

type InferValue<Prop extends PropertyKey, Desc> = Desc extends { get(): any; value: any } ? never : Desc extends { value: infer T } ? Record<Prop, T> : Desc extends { get(): infer T } ? Record<Prop, T> : never;

type DefineProperty<Prop extends PropertyKey, Desc extends PropertyDescriptor> = Desc extends { writable: any; set(val: any): any }
  ? never
  : Desc extends { writable: any; get(): any }
  ? never
  : Desc extends { writable: false }
  ? Readonly<InferValue<Prop, Desc>>
  : Desc extends { writable: true }
  ? InferValue<Prop, Desc>
  : Readonly<InferValue<Prop, Desc>>;

function defineProperty<Obj extends object, Key extends PropertyKey, PDesc extends PropertyDescriptor>(obj: Obj, prop: Key, val: PDesc): asserts obj is Obj & DefineProperty<Key, PDesc> {
  Object.defineProperty(obj, prop, val);
}

defineProperty(Object.prototype, 'watch', {
  enumerable: false,
  configurable: true,
  writable: false,
  value(this: any, prop, handler) {
    let oldval: any = this[prop],
      newval: any = oldval,
      getter = function () {
        return newval;
      },
      setter = function (this: any, val) {
        oldval = newval;
        return (newval = handler.call(this, prop, oldval, val));
      };

    if (delete this[prop]) {
      // can't watch constants
      Object.defineProperty(this, prop, {
        get: getter,
        set: setter,
        enumerable: true,
        configurable: true,
      });
    }
  },
});

export const event = (event: IActionEvent) => {
  return (_target: any, _key: string, descriptor: PropertyDescriptor) => {
    eventHandler(event.target, event.type, actionEventHelper(descriptor, event.meta).value);
  };
};

export const events = (event: IActionEvent) => {
  return (_target: any, _key: string, descriptor: PropertyDescriptor) => {
    eventsHandler(event.target, event.type, actionEventHelper(descriptor, event.meta).value);
  };
};

export const action = (event: IActionEvent) => {
  return (_target: any, _key: string, descriptor: PropertyDescriptor) => {
    actionHandler(event.target, event.type, actionEventHelper(descriptor, event.meta).value);
  };
};

export const actions = (event: IActionEvent) => {
  return (_target: any, _key: string, descriptor: PropertyDescriptor) => {
    actionsHandler(event.target, event.type, actionEventHelper(descriptor, event.meta).value);
  };
};

export const bind = (event: IActionEvent) => {
  return (_target: any, _key: string, descriptor: PropertyDescriptor) => {
    bindHandler(event.target, event.type, actionEventHelper(descriptor, event.meta).value);
  };
};

const actionEventHelper = (descriptor: PropertyDescriptor, meta?: object) => {
  const decorated: Function = descriptor.value;
  descriptor.value = function (event: Event) {
    if (event?.preventDefault) event?.preventDefault();

    return decorated.apply(this, [...Array.from(arguments), meta]);
  };
  return descriptor;
};